import React, { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import compact from 'lodash/compact';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { default as PatientRecordSelect } from '../../../../common/selectors/PatientRecord';
import { default as ProjectMilestoneSelect } from '../../../../common/selectors/ProjectMilestone';
import { default as ParticipationSelect } from '../../../../common/selectors/Participation';
import { default as ProjectSelect } from '../../../../common/selectors/Project';
import List from '../../../../common/components/List';
import Box from '../../../../common/components/primitives/Box';
import Calendar from '../../../../components/Calendar';
import Card from '../../../../components/Card';
import { openMilestoneDialog } from '../../actions';
import PatientProfile from './components/PatientProfile';
import MilestonesList from './components/MilestonesList';
import ProjectTimezone from './components/ProjectTimezone';
import { MilestonesModalActivity } from '../ProjectTable/MilestonesModal';
import useBooleanState from '../../../../utils/hooks/useBooleanState';
import Tabs from '../../../../components/layouts/Tabs';
import CalendarTimeView from '../../../../components/CalendarTimeView';
import Dialog from '../../../../components/Dialog';
import ClinicalEventsList from './components/ClinicalEventsList';
import usePatientClinicalEvents from '../../hooks/usePatientClinicalEvents';

const DashboardCalendar = ({ recipientId, projectId }) => {
  const [activitiesModal, showActivitiesModal, hideActivitiesModal] =
    useBooleanState(false);
  const [activityIds, setActivityIds] = useState([]);

  const { t } = useTranslation();
  const project = useSelector(ProjectSelect.one().whereIdEquals(projectId));
  const milestones = useSelector(
    ProjectMilestoneSelect.all().where({
      projectId,
    }),
  );
  const allPatientRecords = useSelector(
    PatientRecordSelect.all().where({
      projectId,
      recipientId,
    }),
  );

  const dispatch = useDispatch();
  const handleOnOpenMilestone = useCallback(
    ({ milestoneId, activityId, activeKey }) =>
      dispatch(
        openMilestoneDialog({
          recipientId,
          milestoneId,
          activityId,
          activeKey,
        }),
      ),
    [dispatch, recipientId],
  );

  const participationsIds = allPatientRecords.map(
    ({ participationId }) => participationId,
  );
  const [currentParticipationId, setCurrentParticipationId] = useState();
  useEffect(() => {
    if (participationsIds.length > 0) {
      setCurrentParticipationId(participationsIds[0]);
    }
    // NOTE: Call on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const currentPatientRecord = useMemo(() => {
    return allPatientRecords.find(
      ({ participationId }) => participationId === currentParticipationId,
    );
  }, [allPatientRecords, currentParticipationId]);
  const handleOnChangeParticipationId = (val) => setCurrentParticipationId(val);

  const participation = useSelector(
    ParticipationSelect.one().whereIdEquals(currentParticipationId),
  );
  const isDischarged = participation?.isDischarged();

  // Get clinical event activities (those with eventId and clinicalEventsType, not archived)
  const clinicalEventActivities = useMemo(() => {
    if (!currentPatientRecord?.activities) return [];
    return currentPatientRecord.activities.filter(
      (activity) =>
        activity.eventId && activity.clinicalEventsType && !activity.isArchived,
    );
  }, [currentPatientRecord?.activities]);

  // Fetch real clinical events data
  const { clinicalEvents, loading: clinicalEventsLoading } =
    usePatientClinicalEvents(recipientId, projectId, clinicalEventActivities);

  // Transform clinical events into separate arrays by type
  // Also move patientId to top level for compatibility with detail components
  // And enrich with local project/milestone data from activities
  const { appointments, medications, procedures } = useMemo(() => {
    const appointmentsArr = [];
    const medicationsArr = [];
    const proceduresArr = [];

    Object.values(clinicalEvents).forEach((event) => {
      // Find the activity that references this event to get milestoneId
      const activity = clinicalEventActivities.find(
        (a) => a.eventId === event.eventId,
      );
      const activityMilestoneId = activity?.milestoneId;
      const milestone = activityMilestoneId
        ? milestones.find((m) => m._id === activityMilestoneId)
        : null;

      // Move patientId to top level for compatibility with AppointmentDetails/ProcedureDetails
      // Also enrich context with project/milestone info from local data if not present
      const transformedEvent = {
        ...event,
        patientId: event.context?.patientId,
        context: {
          ...event.context,
          // Use backend data if available, otherwise use local data
          projectId: event.context?.projectId || projectId,
          projectLabel: event.context?.projectLabel || project?.name,
          milestoneId: event.context?.milestoneId || activityMilestoneId,
          milestoneLabel:
            event.context?.milestoneLabel || milestone?.getName?.() || null,
        },
      };

      if (event.domain === 'appointment') {
        appointmentsArr.push(transformedEvent);
      } else if (event.domain === 'medication') {
        medicationsArr.push(transformedEvent);
      } else if (event.domain === 'procedure') {
        proceduresArr.push(transformedEvent);
      }
    });

    return {
      appointments: appointmentsArr,
      medications: medicationsArr,
      procedures: proceduresArr,
    };
  }, [clinicalEvents, clinicalEventActivities, milestones, projectId, project]);

  const transformedActivities = useMemo(
    () =>
      compact(
        map(currentPatientRecord?.activities || [], (activity) => {
          const { milestoneId, activityId } = activity;
          const milestone = milestones.find(({ _id }) => _id === milestoneId);
          const isNonActionable = milestone && milestone.isNonActionable();

          if (milestone) {
            // TODO: Add isEmpty prop?
            return {
              ...activity,
              isNonActionable,
              type: 'milestone',
              isDisabled: isDischarged,
              dateStart: activity.dateStart && new Date(activity.dateStart),
              dateEnd: activity.dateEnd && new Date(activity.dateEnd),
              name: milestone.getName(),
              onClick: () => handleOnOpenMilestone({ milestoneId, activityId }),
              // isEmpty: !isNonActionable && nQuestionnaires === 0,
            };
          }

          return null;
        }),
      ),
    [
      currentPatientRecord?.activities,
      handleOnOpenMilestone,
      isDischarged,
      milestones,
    ],
  );

  const handleOnShowMoreActivities = useCallback(
    ({ activitiesIds }) => {
      setActivityIds(activitiesIds);
      showActivitiesModal();
    },
    [setActivityIds, showActivitiesModal],
  );

  const handleHideActivitiesModal = useCallback(() => {
    hideActivitiesModal();
    setActivityIds([]);
  }, [hideActivitiesModal, setActivityIds]);

  const [currentTab, setCurrentTab] = useState('milestones');
  const [jumpToDate, setJumpToDate] = useState();
  const handleOnTabChange = (tab, date) => {
    setJumpToDate(null);
    setCurrentTab(tab);

    if (date) {
      setJumpToDate(date);
    }
  };
  const tabs = [
    {
      value: 'milestones',
      label: t('milestones'),
      content: (
        <Calendar
          className="flex-1"
          activities={transformedActivities}
          clinicalEvents={{
            appointments,
            medications,
            procedures,
          }}
          onClinicalEventClick={handleOnTabChange}
          onShowMoreActivities={handleOnShowMoreActivities}
        />
      ),
    },
    {
      value: 'appointments',
      label: t('appointments'),
      content: (
        <CalendarTimeView events={appointments} jumpToDate={jumpToDate} />
      ),
    },
    {
      value: 'procedures',
      label: t('procedures'),
      content: <CalendarTimeView events={procedures} jumpToDate={jumpToDate} />,
    },
  ];

  return (
    <>
      <div className="cluster-6">
        <div className="stack-6 max-w-sm">
          <Card data-testid="project-timezone" bordered>
            <ProjectTimezone project={project} />
          </Card>
          {currentPatientRecord && (
            <Card data-testid="patient-profile" bordered>
              <PatientProfile
                allPatientRecords={allPatientRecords}
                patientRecord={currentPatientRecord}
                participationsIds={participationsIds}
                currentParticipationId={currentParticipationId}
                onChangeParticipationId={handleOnChangeParticipationId}
              />
            </Card>
          )}
          {currentTab === 'milestones' && currentPatientRecord && (
            <Card data-testid="milestones-list" bordered>
              <MilestonesList
                project={project}
                patientRecord={currentPatientRecord}
                milestones={milestones}
                onMilestoneClick={handleOnOpenMilestone}
                isDischarged={isDischarged}
              />
            </Card>
          )}
          {currentTab === 'appointments' && (
            <ClinicalEventsList
              data-testid="appointments-list"
              events={appointments}
            />
          )}
          {currentTab === 'medications' && (
            <ClinicalEventsList
              data-testid="medications-list"
              events={medications}
            />
          )}
          {currentTab === 'procedures' && (
            <ClinicalEventsList
              data-testid="procedures-list"
              events={procedures}
            />
          )}
        </div>
        <Tabs
          data-testid="milestones-tabs"
          activeKey={currentTab}
          onChange={handleOnTabChange}
          tabs={tabs}
          space={0}
          className="flex-1 overflow-x-hidden"
        />

        {activitiesModal && (
          <Dialog
            size="small"
            title={t('milestones')}
            onOk={handleHideActivitiesModal}
            onCancel={handleHideActivitiesModal}
            visible
          >
            <Box.Primary>
              <List
                dataSource={transformedActivities.filter((activity) =>
                  activityIds.includes(activity.activityId),
                )}
                renderItem={(activity) => (
                  <MilestonesModalActivity
                    key={activity.activityId}
                    patientRecord={currentPatientRecord}
                    activity={activity}
                    onMilestoneClick={() =>
                      handleOnOpenMilestone({
                        milestoneId: activity.milestoneId,
                        activityId: activity.activityId,
                      })
                    }
                    isDischarged={isDischarged}
                  />
                )}
              />
            </Box.Primary>
          </Dialog>
        )}
      </div>
    </>
  );
};

DashboardCalendar.propTypes = {
  recipientId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
};

export default DashboardCalendar;
